// =============================================================================
// OmniHub Home V2 — Dashboard avec widgets personnalisables
// Support complet Light / Dark mode via Tailwind dark: prefix
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import { hasVisitesTerrain } from "../../utils/tenantProfile";
import { useTheme } from "../../contexts/ThemeContext";
import { YearSelector } from "../../components/ui/year-selector";
import {
  Sparkles, TrendingUp, Users, FolderKanban,
  FileText, Euro, Wrench, CheckCircle2, Clock, RefreshCw, Loader2,
  Settings2, X, ChevronRight, BarChart3, PieChart,
  Activity, FolderOpen, Receipt, AlertTriangle, Zap,
  CalendarDays, ArrowUpRight, ArrowDownRight, Minus,
  Smartphone,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// COULEURS
// =============================================================================
const HUB_COLORS = {
  commercial: { bg: "var(--oh-stat-blue-text)", label: "Commercial" },
  conformite: { bg: "#8b5cf6", label: "Conformité" },
  finance: { bg: "var(--oh-stat-amber-text)", label: "Finance" },
  operation: { bg: "var(--oh-stat-orange-text)", label: "Opération" },
  cloture: { bg: "#6b7280", label: "Clôture" },
};

const STATUT_COLORS = {
  prospect: "#71717a", dossier_cree: "var(--oh-stat-blue-text)", dossier_depose: "var(--oh-stat-cyan-text)",
  aides_en_instruction: "var(--oh-stat-amber-text)", aides_validees: "#10b981",
  demande_avance: "#8b5cf6", avance_payee: "var(--oh-stat-purple-text)",
  demande_solde: "#ec4899", paye: "var(--oh-stat-green-text)",
  refuse_anah: "var(--oh-stat-red-text)", archive: "#6b7280",
};

// =============================================================================
// STORAGE DES PREFERENCES WIDGETS
// =============================================================================
const DEFAULT_WIDGETS = [
  "kpis", "monthly_dossiers", "monthly_amo", "hub_distribution",
  "hub_detail", "pipeline", "alertes", "quick_links"
];

const WIDGET_CATALOG = {
  kpis: { label: "KPIs clés", icon: Activity, description: "Chiffres synthétiques" },
  monthly_dossiers: { label: "Dossiers / mois", icon: BarChart3, description: "Nouveaux dossiers par mois" },
  monthly_amo: { label: "Encaissements AMO", icon: Euro, description: "Factures AMO par mois" },
  hub_distribution: { label: "Répartition Hubs", icon: PieChart, description: "Donut par hub" },
  hub_detail: { label: "Statuts par Hub", icon: FolderOpen, description: "Détail statut dans chaque hub" },
  pipeline: { label: "Pipeline", icon: TrendingUp, description: "Tunnel des dossiers" },
  alertes: { label: "Dossiers en attente", icon: Clock, description: "Nombre de jours d'attente" },
  quick_links: { label: "Liens rapides", icon: Zap, description: "Actions contextuelles" },
};

function getWidgetPrefs() {
  try {
    const raw = localStorage.getItem("dashboard_widgets_v2");
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_WIDGETS;
}
function saveWidgetPrefs(widgets) {
  localStorage.setItem("dashboard_widgets_v2", JSON.stringify(widgets));
}

// =============================================================================
// TOOLTIP CUSTOM (theme-aware)
// =============================================================================
const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-slate-500 dark:text-zinc-400 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-900 dark:text-white text-sm font-semibold">
          {isCurrency ? `${p.value.toLocaleString("fr-FR")} EUR` : p.value}
        </p>
      ))}
    </div>
  );
};

// =============================================================================
// WIDGET: KPI CARDS
// =============================================================================
const KPICardsWidget = ({ kpis, onNavigate }) => {
  const trend = kpis.dossiers_mois_precedent > 0
    ? Math.round(((kpis.dossiers_ce_mois - kpis.dossiers_mois_precedent) / kpis.dossiers_mois_precedent) * 100)
    : null;

  const cards = [
    {
      title: "Clients", value: kpis.clients, icon: Users,
      color: "border-blue-500/30 bg-blue-50 dark:bg-blue-500/10",
      iconColor: "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20",
      onClick: () => onNavigate?.("commercial", "clients"),
    },
    {
      title: "Dossiers actifs", value: kpis.dossiers_actifs, icon: FolderKanban,
      subtitle: `${kpis.dossiers_total} total`,
      color: "border-cyan-500/30 bg-cyan-50 dark:bg-cyan-500/10",
      iconColor: "text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-500/20",
      onClick: () => onNavigate?.("commercial", "dossiers"),
    },
    {
      title: "Nouveaux ce mois", value: kpis.dossiers_ce_mois, icon: CalendarDays,
      trend,
      color: "border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10",
      iconColor: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20",
    },
    {
      title: "Contrats AMO", value: kpis.contrats_amo, icon: FileText,
      subtitle: `${kpis.ca_amo_total.toLocaleString("fr-FR")} EUR`,
      color: "border-amber-500/30 bg-amber-50 dark:bg-amber-500/10",
      iconColor: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20",
      onClick: () => onNavigate?.("commercial", "devis-factures"),
    },
    {
      title: "Installateurs", value: kpis.installateurs, icon: Wrench,
      color: "border-orange-500/30 bg-orange-50 dark:bg-orange-500/10",
      iconColor: "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20",
      onClick: () => onNavigate?.("operation", "installateurs"),
    },
    {
      title: "Devis", value: kpis.devis_total, icon: Receipt,
      color: "border-purple-500/30 bg-purple-50 dark:bg-purple-500/10",
      iconColor: "text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20",
      onClick: () => onNavigate?.("commercial", "devis-factures"),
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards-widget">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card
            key={c.title}
            data-testid={`kpi-${c.title.toLowerCase().replace(/\s/g, "-")}`}
            className={`${c.color} border cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg`}
            onClick={c.onClick}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-slate-500 dark:text-zinc-400 text-xs truncate">{c.title}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-0.5">{c.value}</p>
                  {c.subtitle && <p className="text-slate-400 dark:text-zinc-500 text-[11px] mt-0.5">{c.subtitle}</p>}
                  {c.trend !== undefined && c.trend !== null && (
                    <div className={`flex items-center gap-1 mt-1 text-xs ${c.trend >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      {c.trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : c.trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      {c.trend > 0 ? "+" : ""}{c.trend}% vs mois prec.
                    </div>
                  )}
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${c.iconColor}`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// =============================================================================
// WIDGET: MONTHLY DOSSIERS BAR CHART
// =============================================================================
const MonthlyDossiersWidget = ({ data, theme }) => {
  const tickColor = theme === "dark" ? "#a1a1aa" : "#64748b";
  const gridColor = theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)";
  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="monthly-dossiers-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
          Nouveaux dossiers par mois
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="count" fill="#0891b2" radius={[4, 4, 0, 0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: MONTHLY AMO ENCAISSEMENTS
// =============================================================================
const MonthlyAMOWidget = ({ data, theme }) => {
  const tickColor = theme === "dark" ? "#a1a1aa" : "#64748b";
  const gridColor = theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)";
  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="monthly-amo-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <Euro className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          Encaissements AMO / mois
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip content={<ChartTooltip isCurrency />} cursor={{ fill: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="montant" fill="#d97706" radius={[4, 4, 0, 0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: HUB DISTRIBUTION (DONUT)
// =============================================================================
const HubDistributionWidget = ({ data }) => {
  const chartData = Object.entries(data)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: HUB_COLORS[key]?.label || key,
      value,
      fill: HUB_COLORS[key]?.bg || "#6b7280",
    }));

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="hub-distribution-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <PieChart className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          Répartition par Hub
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <RechartsPie>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={3}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.[0] ? (
                  <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 shadow-xl">
                    <p className="text-slate-900 dark:text-white text-sm font-semibold">{payload[0].name}</p>
                    <p className="text-slate-500 dark:text-zinc-400 text-xs">{payload[0].value} dossiers ({Math.round((payload[0].value / total) * 100)}%)</p>
                  </div>
                ) : null
              }
            />
          </RechartsPie>
        </ResponsiveContainer>
        <div className="flex flex-wrap justify-center gap-3 -mt-2">
          {chartData.map((d) => (
            <div key={d.name} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
              <span className="text-xs text-slate-600 dark:text-zinc-400">{d.name}</span>
              <span className="text-xs font-semibold text-slate-800 dark:text-white">{d.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: HUB DETAIL (STATUTS PAR HUB)
// =============================================================================
const HubDetailWidget = ({ hubDetail, onNavigate }) => {
  const hubOrder = ["commercial", "conformite", "finance", "operation", "cloture"];
  const hubStyles = {
    commercial: "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20",
    conformite: "text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20",
    finance: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20",
    operation: "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20",
    cloture: "text-slate-500 dark:text-zinc-400 bg-slate-100 dark:bg-zinc-500/20",
  };

  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5 col-span-full" data-testid="hub-detail-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
          Statuts par Hub
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {hubOrder.map((hubId) => {
            const statuts = hubDetail[hubId];
            if (!statuts?.length) return null;
            const total = statuts.reduce((s, st) => s + st.count, 0);
            const colors = hubStyles[hubId] || hubStyles.cloture;

            return (
              <div
                key={hubId}
                className="bg-slate-50 dark:bg-zinc-800/40 border border-slate-200 dark:border-white/5 rounded-xl p-4 hover:border-slate-300 dark:hover:border-white/10 transition-all cursor-pointer"
                onClick={() => onNavigate?.(hubId === "cloture" ? "commercial" : hubId)}
                data-testid={`hub-detail-${hubId}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors}`}>
                      <FolderKanban className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-slate-800 dark:text-white font-medium text-sm capitalize">{HUB_COLORS[hubId]?.label || hubId}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">{total}</Badge>
                </div>
                <div className="space-y-2">
                  {statuts.map((st) => {
                    const pct = total > 0 ? Math.round((st.count / total) * 100) : 0;
                    return (
                      <div key={st.statut} className="flex items-center gap-2">
                        <div className="w-10 text-right">
                          <span className="text-xs font-medium text-slate-700 dark:text-zinc-300">{st.count}</span>
                        </div>
                        <div className="flex-1 h-2.5 bg-slate-200 dark:bg-zinc-700/50 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: STATUT_COLORS[st.statut] || "#6b7280",
                            }}
                          />
                        </div>
                        <span className="text-xs text-slate-600 dark:text-zinc-400 w-28 truncate">{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: PIPELINE (FUNNEL)
// =============================================================================
const PipelineWidget = ({ pipeline, total }) => {
  if (!pipeline?.length) return null;
  const maxCount = Math.max(...pipeline.map((p) => p.count));

  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="pipeline-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            Dossiers à Facturer
          </CardTitle>
          <Badge variant="secondary" className="text-xs">{total} dossiers</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {pipeline.map((p) => {
          const pct = maxCount > 0 ? (p.count / maxCount) * 100 : 0;
          const color = STATUT_COLORS[p.phase] || "#6b7280";
          return (
            <div key={p.phase} className="flex items-center gap-3 group" data-testid={`pipeline-${p.phase}`}>
              <span className="text-xs text-slate-500 dark:text-zinc-400 w-28 text-right truncate">{p.label}</span>
              <div className="flex-1 flex items-center">
                <div
                  className="h-7 rounded-md flex items-center px-2 transition-all group-hover:brightness-110"
                  style={{
                    width: `${Math.max(pct, 5)}%`,
                    backgroundColor: color,
                    minWidth: 32,
                  }}
                >
                  <span className="text-white text-xs font-bold drop-shadow-sm">{p.count}</span>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: ALERTES — DOSSIERS EN ATTENTE
// =============================================================================
const AlertesWidget = ({ alertes }) => {
  if (!alertes?.length) {
    return (
      <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="alertes-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            Dossiers en attente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 dark:text-zinc-500 text-sm text-center py-4">Aucun dossier en attente</p>
        </CardContent>
      </Card>
    );
  }

  const getBadgeColor = (jours) => {
    if (jours >= 60) return "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20";
    if (jours >= 30) return "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20";
    return "bg-slate-100 dark:bg-zinc-500/15 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-zinc-500/20";
  };

  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="alertes-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            Dossiers en attente
          </CardTitle>
          <Badge variant="secondary" className="text-xs">{alertes.length} dossiers</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-xs text-slate-400 dark:text-zinc-500 pb-1.5 border-b border-slate-100 dark:border-white/5">
            <span>Référence</span>
            <span className="w-32 text-center">Statut</span>
            <span className="w-14 text-right">Jours</span>
          </div>
          {alertes.map((a, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center py-2 hover:bg-slate-50 dark:hover:bg-white/[0.02] rounded px-1">
              <div className="truncate">
                <span className="text-sm text-slate-800 dark:text-zinc-200 font-mono">{a.reference || a.référence || "—"}</span>
                {a.client_nom && (
                  <span className="text-xs text-slate-400 dark:text-zinc-500 ml-2">{a.client_nom}</span>
                )}
              </div>
              <span className="text-[11px] text-slate-600 dark:text-zinc-400 bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded w-32 text-center truncate">{a.label}</span>
              <div className="w-14 text-right">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getBadgeColor(a.jours)}`}>
                  {a.jours}j
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET: QUICK LINKS
// =============================================================================
const QuickLinksWidget = ({ onNavigate }) => {
  const links = [
    { label: "Nouveau dossier", icon: FolderKanban, color: "text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-500/10", hub: "commercial", page: "nouveau-dossier" },
    { label: "Liste dossiers", icon: FolderOpen, color: "text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10", hub: "commercial", page: "dossiers" },
    { label: "Clients", icon: Users, color: "text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-500/10", hub: "commercial", page: "clients" },
    { label: "Devis & Factures", icon: Receipt, color: "text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10", hub: "commercial", page: "devis-factures" },
    { label: "Joey (Conformité)", icon: CheckCircle2, color: "text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10", hub: "conformite", page: "agent-conformité" },
    { label: "Installateurs", icon: Wrench, color: "text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10", hub: "operation", page: "installateurs" },
  ];

  // 🔴 L'ENTREE VERS LE PARCOURS TERRAIN — bornee par CAPABILITE DECLAREE.
  //
  // ⚠️ MESURE (17/09) : AUCUNE navigation interne ne menait a /mobile/visite.
  // La route existe (App.js:74), l'ecran est servi, et il etait INATTEIGNABLE
  // depuis l'application. Un utilisateur dont la PWA ouvre l'ERP n'avait donc
  // AUCUN chemin vers son espace terrain.
  // 🔑 DECLARATION POSITIVE : visible si le tenant a des PROFILS DE VISITE.
  // ⛔ Pas `tenant === "mario_bat"` : la visite terrain est transverse, et
  // coder un slug ici referait l'erreur de `!isOmv`.
  // ⚠️ /mobile/visite est une VRAIE route : on y va par un lien, ⛔ pas par la
  // navigation interne en etat (onNavigate), qui ne sait pas l'atteindre.
  if (hasVisitesTerrain()) {
    links.push({ label: "Visite terrain", icon: Smartphone,
      color: "text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-500/10",
      href: "/mobile/visite", page: "visite-terrain" });
  }

  return (
    <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="quick-links-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          Accès rapides
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {links.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.label}
                data-testid={`quick-link-${link.page}`}
                onClick={() => { if (link.href) { window.location.href = link.href; } else { onNavigate?.(link.hub, link.page); } }}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-white/5 transition-all group text-left ${link.color}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-sm text-slate-700 dark:text-zinc-300 group-hover:text-slate-900 dark:group-hover:text-white truncate">{link.label}</span>
                <ChevronRight className="w-3 h-3 text-slate-300 dark:text-zinc-600 group-hover:text-slate-500 dark:group-hover:text-zinc-400 ml-auto shrink-0" />
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// WIDGET SELECTOR MODAL
// =============================================================================
const WidgetSelector = ({ activeWidgets, onSave, onClose }) => {
  const [selected, setSelected] = useState([...activeWidgets]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm" data-testid="widget-selector-modal">
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-white/5">
          <h3 className="text-slate-900 dark:text-white font-semibold flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            Personnaliser les widgets
          </h3>
          <button onClick={onClose} className="text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          {Object.entries(WIDGET_CATALOG).map(([id, widget]) => {
            const Icon = widget.icon;
            const isActive = selected.includes(id);
            return (
              <button
                key={id}
                onClick={() => toggle(id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  isActive
                    ? "border-cyan-400/40 dark:border-cyan-500/40 bg-cyan-50 dark:bg-cyan-500/10"
                    : "border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-zinc-800/40 hover:border-slate-300 dark:hover:border-white/10"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? "bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400" : "bg-slate-100 dark:bg-zinc-700/50 text-slate-400 dark:text-zinc-500"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isActive ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-zinc-400"}`}>{widget.label}</p>
                  <p className="text-xs text-slate-400 dark:text-zinc-600">{widget.description}</p>
                </div>
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                  isActive ? "border-cyan-500 bg-cyan-500" : "border-slate-300 dark:border-zinc-600"
                }`}>
                  {isActive && <CheckCircle2 className="w-3 h-3 text-white" />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between p-5 border-t border-slate-100 dark:border-white/5">
          <button
            onClick={() => setSelected([...DEFAULT_WIDGETS])}
            className="text-sm text-slate-400 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 transition-colors"
          >
            Reinitialiser
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="text-slate-500 dark:text-zinc-400">Annuler</Button>
            <Button
              onClick={() => { onSave(selected); onClose(); }}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="widget-save-btn"
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
export default function HomePage({ onNavigateToHub, onNavigateToModule }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeWidgets, setActiveWidgets] = useState(getWidgetPrefs);
  const [showSelector, setShowSelector] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const { theme } = useTheme();

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": localStorage.getItem("tenant_id") || "tef",
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/dashboard/v2/kpis`, { headers });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error("Dashboard V2 load error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleSaveWidgets = (widgets) => {
    setActiveWidgets(widgets);
    saveWidgetPrefs(widgets);
  };

  const handleNavigate = (hub, page) => {
    if (page) {
      onNavigateToModule?.(hub, page);
    } else {
      onNavigateToHub?.(hub);
    }
  };

  // Années disponibles (union des 2 séries mensuelles)
  const availableYears = useMemo(() => {
    if (!data) return [];
    const all = [...(data.monthly_dossiers || []), ...(data.monthly_encaissements || [])];
    const years = [...new Set(all.map(m => m.mois?.slice(0, 4)).filter(Boolean))];
    return years.sort();
  }, [data]);

  // Données filtrées par année
  const filteredDossiers = useMemo(() =>
    (data?.monthly_dossiers || []).filter(m => m.mois?.startsWith(selectedYear)),
  [data, selectedYear]);

  const filteredEncaissements = useMemo(() =>
    (data?.monthly_encaissements || []).filter(m => m.mois?.startsWith(selectedYear)),
  [data, selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-zinc-500">
        <AlertTriangle className="w-10 h-10 mb-2" />
        <p>Impossible de charger le dashboard</p>
        <Button variant="outline" size="sm" onClick={loadData} className="mt-3">Reessayer</Button>
      </div>
    );
  }

  const isActive = (id) => activeWidgets.includes(id);

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="home-dashboard-v2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            Tableau de bord
          </h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">Vue d'ensemble temps réel</p>
        </div>
        <div className="flex items-center gap-2">
          <YearSelector
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            availableYears={availableYears}
            accentColor="#0891b2"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSelector(true)}
            className="border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
            data-testid="customize-widgets-btn"
          >
            <Settings2 className="w-4 h-4 mr-1.5" />
            Personnaliser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setRefreshing(true); loadData(); }}
            disabled={refreshing}
            className="border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {isActive("kpis") && <KPICardsWidget kpis={data.kpis} onNavigate={handleNavigate} />}

      {/* Charts Row */}
      {(isActive("monthly_dossiers") || isActive("monthly_amo")) && (
        <div className={`grid gap-4 ${isActive("monthly_dossiers") && isActive("monthly_amo") ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
          {isActive("monthly_dossiers") && <MonthlyDossiersWidget data={filteredDossiers} theme={theme} />}
          {isActive("monthly_amo") && <MonthlyAMOWidget data={filteredEncaissements} theme={theme} />}
        </div>
      )}

      {/* Distribution + Pipeline Row */}
      {(isActive("hub_distribution") || isActive("pipeline")) && (
        <div className={`grid gap-4 ${isActive("hub_distribution") && isActive("pipeline") ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
          {isActive("hub_distribution") && <HubDistributionWidget data={data.hub_distribution} />}
          {isActive("pipeline") && <PipelineWidget pipeline={data.pipeline} total={data.kpis.dossiers_total} />}
        </div>
      )}

      {/* Hub Detail */}
      {isActive("hub_detail") && (
        <HubDetailWidget hubDetail={data.hub_detail} onNavigate={(hub) => handleNavigate(hub)} />
      )}

      {/* Alertes */}
      {isActive("alertes") && <AlertesWidget alertes={data.alertes} />}

      {/* Quick Links */}
      {isActive("quick_links") && <QuickLinksWidget onNavigate={handleNavigate} />}

      {/* Widget Selector Modal */}
      {showSelector && (
        <WidgetSelector
          activeWidgets={activeWidgets}
          onSave={handleSaveWidgets}
          onClose={() => setShowSelector(false)}
        />
      )}
    </div>
  );
}
